import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWebSocket } from '../context/WebSocketContext';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Activity, RefreshCw, Search, Trash2, MoreHorizontal, 
  Download, Zap, MapPin, Clock, Wifi, WifiOff,
  ChevronLeft, ChevronRight, Filter, FileText, Eye, X,
  User, StopCircle, Car, ArrowUpDown, ChevronUp, ChevronDown, Battery
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatEnergy, formatDuration } from '../lib/utils';
import api, { remoteStopTransaction } from '../lib/api';
import usePageTitle from '../hooks/usePageTitle';

// Import extracted components
import { SessionDetailsModal, InvoicePopup, SessionAnalytics, SessionErrorStatusBadge } from '../components/sessions';

// Status translation to Albanian
const getStatusAlbanian = (status) => {
  const statusMap = {
    'Charging': 'Duke Karikuar',
    'Completed': 'Përfunduar',
    'Stopped': 'Ndalur',
    'Error': 'Gabim',
    'Starting': 'Duke Filluar',
    'Finishing': 'Duke Përfunduar',
    'Preparing': 'Përgatitje',
    'Available': 'I Lirë',
    'Faulted': 'Defekt'
  };
  return statusMap[status] || status || '—';
};

// European date format for Albania timezone
const formatEuropeanDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return '—';
  }
};

const Sessions = () => {
  usePageTitle('Sesionet e Karikimit');
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0, energy: 0, revenue: 0 });
  
  // Session details modal
  const [detailsSession, setDetailsSession] = useState(null);
  
  // Invoice popup
  const [invoiceSession, setInvoiceSession] = useState(null);
  
  // Date range filter
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(25);
  
  // Sorting
  const [sortBy, setSortBy] = useState('start_time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchParams] = useSearchParams();

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const SortHeader = ({ column, children }) => (
    <TableHead 
      className="text-zinc-400 cursor-pointer hover:text-white transition-colors select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column ? (
          sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
        ) : (
          <ArrowUpDown className="w-3 h-3 text-zinc-600" />
        )}
      </div>
    </TableHead>
  );

  const sortedSessions = useMemo(() => {
    const rows = [...sessions];

    const toNumber = (value) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : 0;
    };

    const toTimestamp = (value) => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };

    rows.sort((a, b) => {
      let left;
      let right;

      switch (sortBy) {
        case 'start_time':
        case 'end_time':
          left = toTimestamp(a?.[sortBy]);
          right = toTimestamp(b?.[sortBy]);
          break;
        case 'energy_kwh':
          left = toNumber(a?.energy_kwh);
          right = toNumber(b?.energy_kwh);
          break;
        case 'cost_lek':
          left = toNumber(a?.cost_lek ?? a?.total_cost);
          right = toNumber(b?.cost_lek ?? b?.total_cost);
          break;
        case 'user_name':
        case 'charger_name':
        case 'license_plate':
        case 'status':
        case 'id':
          left = (a?.[sortBy] || '').toString().toLowerCase();
          right = (b?.[sortBy] || '').toString().toLowerCase();
          break;
        default:
          left = (a?.[sortBy] || '').toString().toLowerCase();
          right = (b?.[sortBy] || '').toString().toLowerCase();
      }

      if (left < right) return sortOrder === 'asc' ? -1 : 1;
      if (left > right) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [sessions, sortBy, sortOrder]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { 
        skip: (page - 1) * limit, 
        limit,
        exclude_archived: true,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      if (dateFrom) params.from_date = dateFrom;
      if (dateTo) params.to_date = dateTo;
      
      const response = await api.get('/sessions', { params });
      
      if (Array.isArray(response.data)) {
        setSessions(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / limit));
      } else {
        setSessions(response.data.items || response.data);
        setTotalCount(response.data.total || response.data.length);
        setTotalPages(Math.ceil((response.data.total || response.data.length) / limit));
      }
      
      const statsParams = {};
      if (dateFrom) statsParams.from_date = dateFrom;
      if (dateTo) statsParams.to_date = dateTo;

      try {
        const statsRes = await api.get('/sessions/stats', { params: statsParams });
        if (statsRes.data) {
          setStats({
            total: statsRes.data.total_sessions || 0,
            active: statsRes.data.active_sessions || 0,
            completed: statsRes.data.completed_sessions || 0,
            energy: statsRes.data.total_energy_kwh || 0,
            revenue: statsRes.data.total_revenue_lek || 0
          });
        }
      } catch (statsError) {
        console.warn('Failed to fetch session stats:', statsError);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Gabim në ngarkimin e seancave');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, searchQuery, dateFrom, dateTo, sortBy, sortOrder]);

  // WebSocket for real-time updates
  const { connected: isConnected, subscribe } = useWebSocket();
  
  useEffect(() => {
    const unsubscribe = subscribe('*', (event) => {
      const eventType = event.type;
      const data = event.data || {};
      
      if (['session.deleted', 'sessions.bulk_deleted', 'session.created', 'session.started'].includes(eventType)) {
        fetchSessions();
        return;
      }
      
      if (eventType === 'session.completed') {
        fetchSessions();
        return;
      }
      
      if (eventType === 'session.progress' || eventType === 'session.updated') {
        const sessionId = data.session_id;
        if (sessionId) {
          setSessions(prev => prev.map(s => {
            if (s.id === sessionId) {
              return {
                ...s,
                energy_kwh: data.energy_kwh ?? s.energy_kwh,
                cost_lek: data.cost_lek ?? s.cost_lek,
                current_power_kw: data.power_kw ?? data.current_power_kw ?? s.current_power_kw,
                duration_minutes: data.duration_minutes ?? s.duration_minutes,
                soc_percent: data.soc_percent ?? s.soc_percent
              };
            }
            return s;
          }));
        }
      }
    });
    return () => unsubscribe();
  }, [subscribe, fetchSessions]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    const querySearch = searchParams.get('search') || searchParams.get('sessionId') || searchParams.get('charger_id');
    if (querySearch) {
      setSearchQuery(querySearch);
      setPage(1);
    }
  }, [searchParams]);
  
  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchQuery, dateFrom, dateTo, limit]);

  const handleDelete = async (hard = false) => {
    if (!selectedSession) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/sessions/${selectedSession.id}${hard ? '?hard=true' : ''}`);
      toast.success(hard ? 'Seanca u fshi përgjithmonë' : 'Seanca u arkivua');
      setDeleteDialogOpen(false);
      setSelectedSession(null);
      fetchSessions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gabim gjatë fshirjes');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRemoteStop = async (session) => {
    if (!session.transaction_id || !session.charger_id) {
      toast.error('Të dhënat e sesionit janë të paplota');
      return;
    }
    
    const chargerId = session.charger_name || session.charger_id;
    
    const confirmed = window.confirm(
      `Jeni i sigurt që dëshironi të ndaloni karikimin?\n\n` +
      `Sesion: ${session.id.slice(0, 8)}...\n` +
      `Përdoruesi: ${session.user_name || 'N/A'}\n` +
      `Energjia: ${session.energy_kwh?.toFixed(2) || 0} kWh\n\n` +
      `Kjo komandë do të dërgohet direkt te karikuesi.`
    );
    
    if (!confirmed) return;
    
    try {
      const response = await remoteStopTransaction(chargerId, session.transaction_id);
      
      if (response.data.status === 'success') {
        toast.success('Karikimi u ndal me sukses nga admini', {
          description: `Sesioni ${session.id.slice(0, 8)} u ndal`
        });
        setTimeout(fetchSessions, 2000);
      } else {
        toast.warning('Komanda u dërgua', {
          description: response.data.message || 'Duke pritur konfirmimin nga karikuesi'
        });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gabim në ndalimin e karikimit';
      toast.error(errorMsg);
    }
  };

  const handleExport = async () => {
    try {
      const params = { format: 'csv' };
      if (dateFrom) params.from_date = dateFrom;
      if (dateTo) params.to_date = dateTo;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const response = await api.get('/sessions/export', {
        params,
        responseType: 'blob'
      });
      
      let filename = 'AlbMove_Seanca';
      if (dateFrom) filename += `_nga_${dateFrom}`;
      if (dateTo) filename += `_deri_${dateTo}`;
      if (!dateFrom && !dateTo) filename += `_${new Date().toISOString().split('T')[0]}`;
      filename += '.csv';
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Eksportimi u kompletua me sukses');
    } catch (error) {
      toast.error('Gabim gjatë eksportimit');
    }
  };
  
  const handleDownloadInvoice = async (session) => {
    if (!session) return;
    setInvoiceSession({ ...session, _autoDownload: true });
  };
  
  const clearFilters = () => {
    setStatusFilter('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <Activity className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white font-outfit">Sesionet e Karikimit</h1>
            <p className="text-zinc-400 text-sm">Historia dhe menaxhimi i sesioneve</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-full ${
            isConnected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isConnected ? 'Live' : 'Offline'}
          </div>
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Eksporto
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-zinc-500">Total Seanca</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-zinc-500">Aktive Tani</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold text-white">{stats.completed}</p>
                <p className="text-xs text-zinc-500">Të Përfunduara</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-white">{formatEnergy(stats.energy)}</p>
                <p className="text-xs text-zinc-500">Total Energji</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats.revenue)}</p>
                <p className="text-xs text-zinc-500">Total Të Ardhura</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sessions Table */}
      <Card className="bg-zinc-900/50 border-white/10">
        <CardContent className="p-0">
          {/* Filters */}
          <div className="p-4 border-b border-white/5 flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Kërko sesione..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-800 border-white/10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-zinc-800 border-white/10">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statusi" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                <SelectItem value="all">Të gjitha</SelectItem>
                <SelectItem value="Charging">Duke karikuar</SelectItem>
                <SelectItem value="Completed">Të përfunduara</SelectItem>
                <SelectItem value="Stopped">Të ndalura</SelectItem>
                <SelectItem value="Error">Me gabim</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[150px] bg-zinc-800 border-white/10"
                placeholder="Nga data"
              />
              <span className="text-zinc-500">—</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[150px] bg-zinc-800 border-white/10"
                placeholder="Deri data"
              />
            </div>

            {(statusFilter !== 'all' || searchQuery || dateFrom || dateTo) && (
              <Button variant="ghost" onClick={clearFilters} className="text-zinc-400 hover:text-white">
                <X className="w-4 h-4 mr-1" />
                Pastro filtrat
              </Button>
            )}

            <Button variant="ghost" onClick={fetchSessions} className="ml-auto">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Rifresko
            </Button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <SortHeader column="id">Nr.</SortHeader>
                  <SortHeader column="user_name">Klienti</SortHeader>
                  <SortHeader column="license_plate">Targa</SortHeader>
                  <SortHeader column="charger_name">Karikuesi</SortHeader>
                  <SortHeader column="start_time">Fillimi</SortHeader>
                  <SortHeader column="end_time">Mbarimi</SortHeader>
                  <SortHeader column="energy_kwh">Konsumi</SortHeader>
                  <SortHeader column="cost_lek">Fatura</SortHeader>
                  <SortHeader column="status">Statusi</SortHeader>
                  <TableHead className="text-zinc-400 text-right">Veprime</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12">
                      <RefreshCw className="w-6 h-6 animate-spin text-zinc-500 mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : sortedSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-12 text-zinc-500">
                      Nuk ka sesione
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedSessions.map((session, index) => (
                    <TableRow key={session.id} className="border-white/5 hover:bg-white/5">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">
                            {(page - 1) * limit + index + 1}
                          </span>
                          {session.start_mode && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                              session.start_mode === 'Admin' ? 'bg-red-500/20 text-red-400' :
                              session.start_mode === 'QR' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {session.start_mode}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-500" />
                          <span className="text-white">{session.user_name || '—'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Car className="w-4 h-4 text-zinc-500" />
                          <span className="text-zinc-300 font-mono text-sm">
                            {session.license_plate || session.vehicle_plate || '—'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-emerald-500" />
                          <div>
                            <p className="text-white text-sm">{session.charger_name || session.charger_id}</p>
                            {session.connector_id && (
                              <p className="text-xs text-zinc-500">Porta {String.fromCharCode(64 + session.connector_id)}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="text-zinc-300">{formatEuropeanDate(session.start_time)}</p>
                          {session.start_soc !== undefined && session.start_soc !== null && (
                            <p className="text-xs text-blue-400 flex items-center gap-1">
                              <Battery className="w-3 h-3" />
                              {session.start_soc}%
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {session.end_time ? (
                            <>
                              <p className="text-zinc-300">{formatEuropeanDate(session.end_time)}</p>
                              {session.end_soc !== undefined && session.end_soc !== null && (
                                <p className="text-xs text-emerald-400 flex items-center gap-1">
                                  <Battery className="w-3 h-3" />
                                  {session.end_soc}%
                                </p>
                              )}
                            </>
                          ) : (
                            <span className="text-orange-400 text-xs">Në vazhdim...</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-yellow-400 font-medium">
                          {formatEnergy(session.energy_kwh)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="text-emerald-400 font-medium">
                          {formatCurrency(session.cost_lek || session.total_cost)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <SessionErrorStatusBadge
                          session={session}
                          label={getStatusAlbanian(session.status)}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            session.status === 'Charging' ? 'bg-blue-500/20 text-blue-400' :
                            session.status === 'Completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            session.status === 'Stopped' ? 'bg-orange-500/20 text-orange-400' :
                            session.status === 'Error' ? 'bg-red-500/20 text-red-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-800 border-white/10">
                            <DropdownMenuItem
                              onClick={() => setDetailsSession(session)}
                              className="text-zinc-300 hover:!bg-white/10 cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Shiko Detajet
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setInvoiceSession(session)}
                              className="text-zinc-300 hover:!bg-white/10 cursor-pointer"
                            >
                              <FileText className="w-4 h-4 mr-2" />
                              Shiko Faturën
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDownloadInvoice(session)}
                              className="text-emerald-400 hover:!bg-emerald-500/10 hover:!text-emerald-300 cursor-pointer"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Shkarko PDF
                            </DropdownMenuItem>
                            {(session.status === 'Charging' || session.status === 'charging') && session.transaction_id && (
                              <>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem
                                  onClick={() => handleRemoteStop(session)}
                                  className="text-orange-400 hover:!bg-orange-500/10 hover:!text-orange-300 cursor-pointer"
                                >
                                  <StopCircle className="w-4 h-4 mr-2" />
                                  Ndal Karikimin (Admin)
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator className="bg-white/10" />
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedSession(session);
                                setDeleteDialogOpen(true);
                              }}
                              className="text-red-400 hover:!bg-red-500/10 hover:!text-red-300 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Fshi
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Pagination */}
          {totalPages > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
              <p className="text-sm text-zinc-500">
                Duke shfaqur {Math.min((page - 1) * limit + 1, totalCount)}-{Math.min(page * limit, totalCount)} nga {totalCount} seanca
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className={page === pageNum ? 'bg-primary' : ''}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Analytics Section */}
      <SessionAnalytics />

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Fshi Seancën</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Zgjidh llojin e fshirjes për këtë seancë.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-sm text-zinc-300">
                <strong>Arkivo:</strong> Seanca do të fshihet nga lista por do të ruhet në arkiv.
              </p>
            </div>
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-300">
                <strong>Fshi Përgjithmonë:</strong> Seanca do të fshihet plotësisht dhe nuk mund të rikthehet.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Anulo
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => handleDelete(false)}
              disabled={deleteLoading}
            >
              Arkivo
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(true)}
              disabled={deleteLoading}
            >
              {deleteLoading ? 'Duke fshirë...' : 'Fshi Përgjithmonë'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Details Modal */}
      <SessionDetailsModal 
        session={detailsSession} 
        onClose={() => setDetailsSession(null)}
        onRemoteStop={(session) => {
          handleRemoteStop(session);
          setDetailsSession(null);
        }}
      />

      {/* Invoice Quick View Popup */}
      <InvoicePopup 
        session={invoiceSession}
        onClose={() => setInvoiceSession(null)}
      />
    </div>
  );
};

export default Sessions;
